import { Tile } from './types.js';

export const TILE_SIZE = 24; // pixels
export const GAME_SPEED_MS = 150; // Update every 150ms
export const PLAYER_BOOSTED_SPEED_MS = 100;
export const INITIAL_LIVES = 3;
export const SPIRIT_COUNT = 4;
export const AI_THINK_INTERVAL = 500; // ms

export const SCORE = {
    FRAGMENT: 10,
    BLOOM: 50,
    SPIRIT: 200,
    POWERUP: 30,
};

export const POWER_UP_DURATION = {
    SPIRIT_VULNERABLE_MS: 8000,
    VULNERABLE_BLINK_DURATION_MS: 3000,
    SPEED_BOOST_MS: 6000,
    SPIRIT_SLOWDOWN_MS: 6000,
};

export const AI_MODE_DURATION = {
    CHASE: 20000, // 20 seconds
    SCATTER: 7000, // 7 seconds
};

// 1=Wall, 0=Empty, 2=Fragment, 3=Bloom, 4=Speed, 5=Slow
const LEVEL_1_RAW = [
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
  [1, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2, 2, 2, 2, 2, 2, 3, 1],
  [1, 2, 1, 1, 2, 1, 1, 1, 2, 1, 2, 1, 1, 1, 2, 1, 1, 2, 1],
  [1, 3, 1, 1, 2, 1, 1, 1, 2, 1, 2, 1, 1, 1, 2, 1, 1, 2, 1],
  [1, 2, 2, 2, 2, 2, 4, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1],
  [1, 2, 1, 1, 2, 1, 2, 1, 1, 1, 1, 1, 2, 1, 2, 1, 1, 2, 1],
  [1, 2, 2, 2, 2, 1, 2, 2, 2, 1, 2, 2, 2, 1, 2, 2, 2, 2, 1],
  [1, 1, 1, 1, 2, 1, 1, 1, 0, 1, 0, 1, 1, 1, 2, 1, 1, 1, 1],
  [0, 0, 0, 1, 2, 1, 0, 0, 0, 0, 0, 0, 0, 1, 2, 1, 0, 0, 0],
  [1, 1, 1, 1, 2, 1, 0, 1, 1, 0, 1, 1, 0, 1, 2, 1, 1, 1, 1],
  [2, 2, 2, 2, 2, 0, 0, 1, 0, 0, 0, 1, 0, 0, 2, 2, 2, 2, 2],
  [1, 1, 1, 1, 2, 1, 0, 1, 1, 1, 1, 1, 0, 1, 2, 1, 1, 1, 1],
  [0, 0, 0, 1, 2, 1, 0, 0, 0, 0, 0, 0, 0, 1, 2, 1, 0, 0, 0],
  [1, 1, 1, 1, 5, 1, 0, 1, 1, 1, 1, 1, 0, 1, 2, 1, 1, 1, 1],
  [1, 2, 2, 2, 2, 2, 2, 2, 2, 1, 2, 2, 2, 2, 2, 2, 2, 2, 1],
  [1, 2, 1, 1, 2, 1, 1, 1, 2, 1, 2, 1, 1, 1, 2, 1, 1, 2, 1],
  [1, 3, 2, 1, 2, 2, 2, 2, 2, 0, 2, 2, 2, 2, 2, 1, 2, 3, 1],
  [1, 1, 2, 1, 2, 1, 2, 1, 1, 1, 1, 1, 2, 1, 2, 1, 2, 1, 1],
  [1, 2, 2, 2, 2, 1, 2, 2, 2, 1, 2, 2, 2, 1, 2, 2, 2, 2, 1],
  [1, 2, 1, 1, 1, 1, 1, 1, 2, 1, 2, 1, 1, 1, 1, 1, 1, 2, 1],
  [1, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 1],
  [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
];

const mapRawToLayout = (raw) => 
    raw.map(row => 
        row.map(cell => {
            switch(cell) {
                case 1: return Tile.WALL;
                case 2: return Tile.FRAGMENT;
                case 3: return Tile.BLOOM;
                case 4: return Tile.SPEED_BOOST;
                case 5: return Tile.SPIRIT_SLOWDOWN;
                default: return Tile.EMPTY;
            }
        })
    );

const LEVEL_1_LAYOUT = mapRawToLayout(LEVEL_1_RAW);

export const LEVEL_CONFIGS = [
    {
        layout: LEVEL_1_LAYOUT,
        playerStart: { x: 9, y: 16 },
        spiritSpawns: [{ x: 8, y: 10 }, { x: 9, y: 10 }, { x: 10, y: 10 }, { x: 11, y: 10 }]
    },
];

export const GRID_HEIGHT = LEVEL_1_LAYOUT.length;
export const GRID_WIDTH = LEVEL_1_LAYOUT[0].length;

export const SPIRIT_PERSONALITIES = ['CHASER', 'AMBUSHER', 'FLANKER', 'ERRATIC'];

export const SPIRIT_SCATTER_TARGETS = [
    { x: GRID_WIDTH - 2, y: 1 }, // Top-right
    { x: 1, y: 1 }, // Top-left
    { x: GRID_WIDTH - 2, y: GRID_HEIGHT - 2 }, // Bottom-right
    { x: 1, y: GRID_HEIGHT - 2 }, // Bottom-left
];